/**
 * AutotileResolver — Extracted from TileMeta.
 *
 * Handles all tile resolution logic:
 *   - Autotile bitmask → tile ID (4-bit, 47-blob, road)
 *   - Random variant selection
 *   - Wall position resolution
 *   - 8-bit → 4-bit bitmask reduction
 *
 * Operates on terrain groups and wall sets owned by TileMeta.
 * Does not store data — receives it via method arguments or constructor refs.
 */

import { AUTOTILE_NONE, AUTOTILE_47 } from './TileMeta.js';

export class AutotileResolver {
  /**
   * @param {Map<string, Object>} terrainGroups — ref to TileMeta._terrainGroups
   * @param {Map<string, Object>} wallSets — ref to TileMeta._wallSets
   */
  constructor(terrainGroups, wallSets) {
    this._terrainGroups = terrainGroups;
    this._wallSets = wallSets;
  }

  /**
   * Resolve a terrain + neighbor bitmask to the correct tile ID.
   * @param {string} terrainId
   * @param {number} bitmask — 4-bit or 8-bit depending on terrain format
   * @returns {number} Tile ID
   */
  resolveAutotile(terrainId, bitmask) {
    const tg = this._terrainGroups.get(terrainId);
    if (!tg) return 0;

    if (tg.autotileFormat === AUTOTILE_NONE || !tg.variants) {
      return tg.centerTile;
    }

    // Check variant mapping first (mask 0 = isolated, which may have its own tile)
    const variant = tg.variants[bitmask];
    if (variant != null) return variant;

    // No variant for this mask — use center tile
    if (bitmask === 0) {
      return tg.centerTile;
    }

    // Fallback: for 47-blob, reduce to 4-bit and try again
    if (tg.autotileFormat === AUTOTILE_47) {
      const reduced = this.reduce8to4(bitmask);
      const fallback = tg.variants[reduced];
      if (fallback != null) return fallback;
    }

    return tg.centerTile;
  }

  /**
   * Pick a random variant of a terrain's center tile.
   * @param {string} terrainId
   * @param {Function} rngFn — () => 0..1
   * @returns {number} Tile ID
   */
  resolveVariant(terrainId, rngFn) {
    const tg = this._terrainGroups.get(terrainId);
    if (!tg) return 0;
    if (!tg.randomVariants || tg.randomVariants.length === 0) return tg.centerTile;
    if (rngFn() < 0.6) return tg.centerTile;
    const idx = Math.floor(rngFn() * tg.randomVariants.length);
    return tg.randomVariants[idx];
  }

  /**
   * Resolve a wall set + position to a tile ID.
   * @param {string} wallSetId
   * @param {string} position — 'top', 'bottom', 'left', 'right', 'cornerTL', etc.
   * @returns {number} Tile ID
   */
  resolveWall(wallSetId, position) {
    const ws = this._wallSets.get(wallSetId);
    if (!ws) return 0;
    return ws[position] ?? ws.top ?? 0;
  }

  /**
   * Reduce 8-bit blob bitmask to 4-bit cardinal.
   * Strips diagonal bits: N(2→1), E(16→2), S(64→4), W(8→8).
   */
  reduce8to4(bitmask8) {
    let mask4 = 0;
    if (bitmask8 & 2)  mask4 |= 1;
    if (bitmask8 & 16) mask4 |= 2;
    if (bitmask8 & 64) mask4 |= 4;
    if (bitmask8 & 8)  mask4 |= 8;
    return mask4;
  }
}
